/* ============================================================
   Capital stack arithmetic.

   An African logistics venture is rarely funded from one place. A
   typical file is a commercial senior loan, a development-finance or
   mezzanine tranche on softer terms, sponsor equity, and — often — a
   grant or blended-finance layer that carries no return at all. Each
   layer has its own cost, its own tenor, and its own claim on the same
   cash.

   The mistake this exists to prevent is averaging them. A sponsor who
   knows only "we raised $2m at about 12%" cannot answer the question a
   credit committee actually asks, which is whether the operation covers
   its debt service in the year the grace period ends. That is usually a
   different and much worse year than year one.

   Everything here is arithmetic on figures the user supplies. There are
   no assumed rates, no country defaults, and no benchmark tables — this
   module invents nothing. What it contributes is the sequencing: which
   layer is paid when, and what that does to coverage over time.

   It is not a credit decision and not an offer of terms.
   ============================================================ */

/* Coverage bands. Deliberately the same thresholds the Financeability
   Pack uses — an operator who sees "Tight" in one tool and "Acceptable"
   in the other for the same 1.2x has learnt nothing except that the
   tools disagree. */
export const DSCR_BANDS = [
  { min: 1.5, label: 'Comfortable', tone: 'var(--ca-positive)', note: 'Most lenders will engage on these terms.' },
  { min: 1.25, label: 'Acceptable', tone: 'var(--ca-chart-2)', note: 'Above the covenant most term sheets set.' },
  { min: 1.0, label: 'Tight', tone: 'var(--ca-warning)', note: 'Servicing debt, but with no headroom for a bad month.' },
  { min: -Infinity, label: 'Distressed', tone: 'var(--ca-danger)', note: 'Earnings do not cover debt service.' }
];

export const NO_DEBT_BAND = {
  label: 'No debt service',
  tone: 'var(--ca-positive)',
  note: 'Nothing to cover. Coverage is not the constraint here.'
};

export function dscrBand(dscr) {
  if (dscr === Infinity) return NO_DEBT_BAND;
  if (!Number.isFinite(dscr)) return DSCR_BANDS[DSCR_BANDS.length - 1];
  return DSCR_BANDS.find((b) => dscr >= b.min);
}

/* The layers, in the order they rank on the cash. Order matters for
   display and for the explanation; it is not a modelling assumption. */
export const LAYERS = [
  {
    id: 'senior',
    label: 'Senior debt',
    blurb: 'Commercial bank or asset finance. First claim on cash and on security.',
    kind: 'debt',
    accent: 'var(--ca-chart-1)'
  },
  {
    id: 'mezz',
    label: 'DFI / mezzanine debt',
    blurb: 'Development finance or subordinated debt. Longer, softer, and paid after senior.',
    kind: 'debt',
    accent: 'var(--ca-chart-2)'
  },
  {
    id: 'equity',
    label: 'Sponsor equity',
    blurb: 'Your own money and any co-investor. Paid last, and only out of what is left.',
    kind: 'equity',
    accent: 'var(--ca-chart-6)'
  },
  {
    id: 'grant',
    label: 'Grant / concessional',
    blurb: 'Blended finance, challenge funds, catalytic capital. No return expected.',
    kind: 'grant',
    accent: 'var(--ca-chart-4)'
  }
];

/**
 * Level annual payment on an amortising loan.
 *
 * Two edge cases that a naive PMT gets wrong, both of which are ordinary
 * in this sector rather than exotic: a genuinely interest-free
 * concessional tranche divides by zero in the standard formula, and a
 * zero-year tenor is a data-entry state rather than a loan.
 */
export function levelPayment(principal, annualRate, years) {
  if (!(principal > 0) || !(years > 0)) return 0;
  const r = annualRate / 100;
  if (r === 0) return principal / years;
  return (principal * r) / (1 - Math.pow(1 + r, -years));
}

/**
 * Debt service for one layer in a given year of the loan's life.
 *
 * During grace, only interest is paid — that is the whole point of
 * grace, and the reason coverage looks healthy right up until it stops
 * being. After grace the principal amortises over the REMAINING tenor,
 * not the original one, which is what makes the step up sharp.
 */
export function layerService(layer, year) {
  const { amount, rate, tenor, grace } = layer;
  if (!(amount > 0) || !(tenor > 0)) return { interest: 0, principal: 0, total: 0 };
  if (year > tenor) return { interest: 0, principal: 0, total: 0 };

  const r = (rate || 0) / 100;
  const g = Math.min(grace || 0, tenor);
  const amortYears = tenor - g;

  /* Grace covering the whole tenor is a bullet: interest throughout, and
     the entire principal at maturity.

     This was the worst bug in the file, and it failed in the direction
     that matters. Amortising over zero years made levelPayment return 0,
     so the principal was serviced in no year at all — a $1m loan at 10%
     over five years with five years' grace reported a worst-year DSCR of
     2.00, band "Comfortable", with the $1m balloon appearing nowhere.
     The tool that exists to expose the post-grace cliff called the most
     dangerous structure it can model its safest.

     Modelled rather than rejected, because a bullet is an ordinary DFI
     structure and the honest reading of one is exactly what this tool is
     for: a final year where service is the whole principal and coverage
     collapses. That is now the loudest number on the page instead of the
     quietest. */
  if (amortYears <= 0) {
    const interest = amount * r;
    return year < tenor
      ? { interest, principal: 0, total: interest }
      : { interest, principal: amount, total: interest + amount };
  }

  if (year <= g) {
    const interest = amount * r;
    return { interest, principal: 0, total: interest };
  }

  const payment = levelPayment(amount, rate || 0, amortYears);

  /* Walk the balance forward rather than using a closed form. The
     closed form is easy to get subtly wrong at the boundary, and this
     runs over a handful of years on a click. */
  let balance = amount;
  for (let y = g + 1; y < year; y++) {
    const int = balance * r;
    balance = Math.max(0, balance - (payment - int));
  }
  const interest = balance * r;
  const principal = Math.min(payment - interest, balance);
  return { interest, principal, total: interest + principal };
}

/**
 * The blended cost of the stack.
 *
 * Grant money is included in the denominator at zero cost, which is the
 * entire reason a blended structure is worth assembling — leaving it out
 * would report a number that flatters nothing and explains nothing.
 * Equity is costed at the sponsor's own target return, because the
 * alternative is pretending equity is free.
 */
export function blendedCost(layers, equityReturn) {
  let weighted = 0;
  let total = 0;
  for (const l of layers) {
    const amount = l.amount || 0;
    if (amount <= 0) continue;
    total += amount;
    if (l.kind === 'debt') weighted += amount * ((l.rate || 0) / 100);
    else if (l.kind === 'equity') weighted += amount * ((equityReturn || 0) / 100);
    /* grant contributes 0 to the numerator, by definition */
  }
  return total > 0 ? (weighted / total) * 100 : 0;
}

/**
 * Assemble the stack and read it.
 *
 * `horizon` is how many years of coverage to walk. The worst year is
 * found rather than assumed, because which year is worst depends on the
 * interaction of two grace periods and two tenors and is not something
 * a sponsor should have to work out on paper.
 */
export function buildStack(input) {
  const { uses, ebitda, equityReturn, layers, horizon = 10 } = input;

  const sources = layers.reduce((sum, l) => sum + (l.amount || 0), 0);
  const gap = uses - sources;

  const debt = layers.filter((l) => l.kind === 'debt').reduce((s, l) => s + (l.amount || 0), 0);
  const equity = layers.filter((l) => l.kind === 'equity').reduce((s, l) => s + (l.amount || 0), 0);
  const grant = layers.filter((l) => l.kind === 'grant').reduce((s, l) => s + (l.amount || 0), 0);

  /* Gearing on total capital, stated as such. "Debt to equity" means
     different things to different lenders and the ambiguity is not
     worth inheriting. */
  const gearing = sources > 0 ? (debt / sources) * 100 : 0;
  const grantShare = sources > 0 ? (grant / sources) * 100 : 0;

  const schedule = [];
  for (let year = 1; year <= horizon; year++) {
    let interest = 0;
    let principal = 0;
    const perLayer = {};
    for (const l of layers) {
      if (l.kind !== 'debt') continue;
      const s = layerService(l, year);
      perLayer[l.id] = s.total;
      interest += s.interest;
      principal += s.principal;
    }
    const service = interest + principal;
    const dscr = service > 0 ? ebitda / service : Infinity;
    schedule.push({ year, interest, principal, service, dscr, perLayer });
  }

  /* Only years with debt outstanding count. Once everything is repaid
     DSCR is Infinity, and letting that win "best year" or letting the
     post-repayment years dilute the reading would be an answer to a
     question nobody asked. */
  const servicedYears = schedule.filter((y) => y.service > 0);
  const worst = servicedYears.length
    ? servicedYears.reduce((a, b) => (b.dscr < a.dscr ? b : a))
    : null;

  const peakService = servicedYears.length ? Math.max(...servicedYears.map((y) => y.service)) : 0;
  const totalInterest = schedule.reduce((s, y) => s + y.interest, 0);

  const cost = blendedCost(layers, equityReturn);

  /* The year-one reading on its own is the trap this tool exists to
     expose, so it is surfaced alongside the worst year rather than
     instead of it. */
  const firstYear = schedule[0] || null;
  const graceCliff =
    firstYear && worst && worst.year > 1 && firstYear.dscr > worst.dscr
      ? { from: firstYear, to: worst }
      : null;

  return {
    sources,
    uses,
    gap,
    debt,
    equity,
    grant,
    gearing,
    grantShare,
    cost,
    schedule,
    worst,
    firstYear,
    peakService,
    totalInterest,
    graceCliff,
    worstBand: worst ? dscrBand(worst.dscr) : NO_DEBT_BAND
  };
}

/* Observations, in the order a reader benefits from them. Each is a
   statement about the numbers entered, not advice about what to do. */
export function readStack(r) {
  const notes = [];

  if (Math.abs(r.gap) > 0.5) {
    notes.push(
      r.gap > 0
        ? {
            tone: 'var(--ca-danger)',
            label: 'Unfunded',
            text: 'Sources do not meet uses. The stack as entered leaves a hole that has to close before anything else in this reading matters.'
          }
        : {
            tone: 'var(--ca-chart-2)',
            label: 'Over-raised',
            text: 'Sources exceed uses. Not a problem, but the surplus is carrying a cost — check it is deliberate rather than a rounding artefact.'
          }
    );
  }

  if (r.graceCliff) {
    notes.push({
      tone: 'var(--ca-warning)',
      label: 'Grace cliff',
      text: `Coverage is healthiest in year one and worst in year ${r.graceCliff.to.year}, when principal starts falling due. A file built on the year-one number is a file that has not been read past the grace period.`
    });
  }

  if (r.gearing >= 80) {
    notes.push({
      tone: 'var(--ca-danger)',
      label: 'Thin equity',
      text: `Debt is ${Math.round(r.gearing)}% of the stack. Most lenders want to see sponsor money at genuine risk before they put theirs there.`
    });
  } else if (r.gearing <= 25 && r.debt > 0) {
    notes.push({
      tone: 'var(--ca-chart-2)',
      label: 'Lightly geared',
      text: `Debt is only ${Math.round(r.gearing)}% of the stack. Coverage will look comfortable, but the sponsor is funding most of the growth themselves.`
    });
  }

  if (r.grantShare >= 15) {
    notes.push({
      tone: 'var(--ca-positive)',
      label: 'Concessional weight',
      text: `${Math.round(r.grantShare)}% of the stack carries no return, which is what pulls the blended cost to ${r.cost.toFixed(1)}%. Worth stating plainly in the file — it is the strongest thing in it.`
    });
  }

  if (r.equity === 0 && r.debt > 0) {
    notes.push({
      tone: 'var(--ca-danger)',
      label: 'No sponsor equity',
      text: 'A stack with no equity layer has nobody taking first loss. This is the single most common reason a credit committee stops reading.'
    });
  }

  return notes;
}
